import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  getDirection,
  getLanguageFromPath,
  isPublicPath,
  normalizeLanguage,
} from './config';

// English is bundled, not fetched. Two reasons:
//
//  1. react-snap prerenders '/', '/terms' and '/PrivacyPolicy' to static HTML
//     at build time. A network-loaded catalogue is not resolved when the
//     snapshot is taken, so those pages would serialise as raw translation
//     keys — which is what the crawler would then index.
//  2. It is the fallbackLng. Every missing key in ar/ur resolves through it,
//     so it has to be present synchronously or the fallback itself suspends.
//
// ar/ur are code-split instead (see the backend below): a visitor reading
// English never downloads them.
import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enValidation from './locales/en/validation.json';
import enSeo from './locales/en/seo.json';
import enLanding from './locales/en/landing.json';
import enLegal from './locales/en/legal.json';


/**
 * Which language to start in, in priority order.
 *
 * This replaces i18next-browser-languagedetector. That package is ~7 KB in
 * the main bundle and its own path lookup is unusable here anyway: it returns
 * the first segment of ANY url, so '/dashboard' detects as the language
 * "dashboard", which i18next then rejects as unsupported and replaces with
 * English — silently discarding a user's stored Arabic on every app route.
 * Once that had to be hand-written, the remaining three lookups were a few
 * lines each.
 *
 * Order matters:
 *  1. the URL, because '/ar/terms' is a public address for the Arabic page and
 *     a link shared into a WhatsApp group must open in the language it names;
 *  2. the stored choice, so a returning user keeps it on unprefixed routes;
 *  3. the browser, so a first-time Arabic speaker lands in Arabic without
 *     hunting for the switcher.
 *
 * Nothing is written back here — see rememberLanguage for why a URL-derived
 * language must not become a preference.
 */
function detectInitialLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  const fromPath = getLanguageFromPath(window.location.pathname);
  if (fromPath) return fromPath;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
  } catch {
    // Private mode / storage disabled — fall through to the browser.
  }

  for (const tag of window.navigator.languages || [window.navigator.language]) {
    const base = normalizeLanguageTag(tag);
    if (base && SUPPORTED_LANGUAGES.includes(base)) return base;
  }

  return DEFAULT_LANGUAGE;
}

/** 'ar-AE' -> 'ar'. Returns null for anything unusable. */
function normalizeLanguageTag(tag) {
  if (!tag) return null;
  return String(tag).toLowerCase().split('-')[0];
}

/**
 * Every namespace the app ships. Adding one means: create the three JSON
 * files and add it here — plus to BUNDLED_NAMESPACES below if a prerendered
 * page needs it.
 */
export const NAMESPACES = [
  // Bundled (see below)
  'common',
  'nav',
  'validation',
  'seo',
  'landing',
  'legal',
  // Fetched on demand
  'auth',
  'dashboard',
  'jobcard',
  'crm',
  'technician',
  'bay',
  'services',
  'billing',
  'mail',
  'booking',
  'users',
  'organization',
  'components',
  'notifications',
  'errors',
  'camera',
];

export const DEFAULT_NS = 'common';

/**
 * The English namespaces that ship inside the main bundle.
 *
 * The line is drawn at what a PRERENDERED page renders. react-snap snapshots
 * '/', '/terms' and '/PrivacyPolicy' at build time; anything those pages read
 * has to resolve synchronously, or the crawler indexes a page of raw
 * translation keys. That is the landing page, the legal pages, the public
 * chrome, and the meta tags — hence common/nav/validation/seo/landing/legal.
 *
 * Everything else belongs to authenticated routes that are already code-split,
 * so their English arrives as one more small chunk beside the route's own and
 * costs the marketing pages nothing.
 */
const bundledEnglish = {
  common: enCommon,
  nav: enNav,
  validation: enValidation,
  seo: enSeo,
  landing: enLanding,
  legal: enLegal,
};

/**
 * Reflects the active language onto <html>, which is what actually drives the
 * UI: `dir` flips every logical CSS property and Tailwind's `rtl:` variants,
 * and `lang` selects the Arabic-script font stack (see index.css) and tells
 * screen readers which voice to use.
 *
 * The same two attributes are written by the pre-paint script in
 * public/index.html so the first frame is already correct; this keeps them in
 * sync afterwards. Deliberately a DOM side effect rather than React state —
 * for the same reason the theme is: React's output must not depend on
 * localStorage, or every prerendered page becomes a hydration mismatch.
 */
export function applyLanguageToDocument(language) {
  if (typeof document === 'undefined') return;
  const lang = normalizeLanguage(language);
  const root = document.documentElement;
  root.setAttribute('lang', lang);
  root.setAttribute('dir', getDirection(lang));
  ensureArabicFont(lang);
}

// Matches the id used by the pre-paint script in public/index.html, so a
// visitor who arrived already in Arabic does not get a second copy of the tag.
const ARABIC_FONT_ID = 'jc-arabic-font';
const ARABIC_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap';

/**
 * Requests the Arabic-script font the first time it is needed.
 *
 * Loading it up front would cost every English visitor a font request for
 * glyphs they never render, which is the same reasoning that keeps Roboto 300
 * out of index.html. Once injected it stays: switching back to English and
 * then to Arabic again should not re-download it.
 */
function ensureArabicFont(lang) {
  if (lang === 'en') return;
  if (document.getElementById(ARABIC_FONT_ID)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.id = ARABIC_FONT_ID;
  link.href = ARABIC_FONT_HREF;
  document.head.appendChild(link);
}

i18n
  // Loads ar/ur catalogues on demand. The template literal is static enough
  // for webpack to build a context out of ./locales, so each language ends up
  // in its own chunk instead of the main bundle.
  .use(
    resourcesToBackend(
      (language, namespace) => import(`./locales/${language}/${namespace}.json`)
    )
  )
  .use(initReactI18next)
  .init({
    lng: detectInitialLanguage(),
    resources: { en: bundledEnglish },
    // Tells i18next that `resources` is a seed, not the whole story, so it
    // still asks the backend for languages it does not already hold.
    // Without this the bundled `en` would switch the backend off entirely.
    partialBundledLanguages: true,

    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    // 'ar-AE' and 'ar-EG' are both just 'ar' to us; without this i18next
    // would look for an 'ar-AE' catalogue that does not exist.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,

    ns: NAMESPACES,
    defaultNS: DEFAULT_NS,
    fallbackNS: DEFAULT_NS,

    interpolation: {
      // React escapes for us; doing it twice turns an apostrophe into &#39;.
      escapeValue: false,
    },

    returnEmptyString: false,
    debug: false,

    // i18next v25 prints a Locize advert to console.info on every init. It is
    // a supported opt-out, and a vendor ad has no business in a workshop's
    // browser console (or in the test output).
    showSupportNotice: false,
  });

// Keep <html lang/dir> honest for the rest of the session.
i18n.on('languageChanged', applyLanguageToDocument);

/**
 * Persists a language the user actually asked for.
 *
 * The detector's own `caches` is switched off, because it would write back
 * whatever it detected — including a language read out of the URL. One visit to
 * a shared '/ar' link would then silently repoint the visitor's entire app to
 * Arabic. Storing here instead means only a deliberate choice sticks.
 */
function rememberLanguage(lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Private mode / storage disabled — the choice applies for this session.
  }
}

/**
 * Switches language without flashing a loading state.
 *
 * `changeLanguage` alone would swap the active language before the catalogue
 * arrives, so every mounted component re-renders against an empty store and
 * suspends — the whole route tree drops to App's spinner for the length of a
 * network round trip. Loading first means the swap is synchronous.
 */
export async function changeLanguage(language) {
  const lang = normalizeLanguage(language);
  // Recorded even when the language is unchanged: arriving on '/ar' and then
  // pressing "العربية" is a real choice, and without this it would be
  // forgotten the moment they navigate to an unprefixed page.
  rememberLanguage(lang);
  if (lang === normalizeLanguage(i18n.resolvedLanguage)) return;
  try {
    await i18n.loadLanguages(lang);
  } catch {
    // Fall through: changeLanguage still works, it just suspends once, and
    // missing keys fall back to the bundled English rather than to nothing.
  }
  await i18n.changeLanguage(lang);
}

/**
 * Follows the language in the URL after a client-side navigation.
 *
 * The detector only runs at init, so moving from '/terms' to '/ar/terms'
 * through the router would otherwise leave the page in English. Does NOT
 * persist — see rememberLanguage.
 */
export async function syncLanguageToPath(pathname) {
  const fromPath = getLanguageFromPath(pathname);
  // An unprefixed path means "English" only for the public pages; on an app
  // route it means nothing at all, so the current language must stand.
  const target = fromPath || (isPublicPath(pathname) ? DEFAULT_LANGUAGE : null);
  if (!target) return;
  if (target === normalizeLanguage(i18n.resolvedLanguage)) return;
  try {
    await i18n.loadLanguages(target);
  } catch {
    /* fall back to English copy rather than blocking navigation */
  }
  await i18n.changeLanguage(target);
}

export default i18n;
